import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HaloBridge } from '../src';
import './App.css';
import {
  Button,
  Card,
  CodeBlock,
  Divider,
  Empty,
  Field,
  ItemCard,
  ItemMeta,
  Items,
  List,
  Row,
  Stack,
  TextArea,
  TextInput,
} from './components/ui';

const ORIGIN_HISTORY_KEY = 'halo-bridge-example-origin-history';
const ORIGIN_HISTORY_MAX = 20;

function loadOriginHistory(): string[] {
  try {
    const raw = localStorage.getItem(ORIGIN_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
      .slice(0, ORIGIN_HISTORY_MAX);
  } catch {
    return [];
  }
}

function saveOriginHistory(history: string[]) {
  try {
    localStorage.setItem(
      ORIGIN_HISTORY_KEY,
      JSON.stringify(history.slice(0, ORIGIN_HISTORY_MAX))
    );
  } catch {
    // ignore
  }
}

const requests = {
  getTokens: {},
  getContacts: {},
  getSessions: {},
  shareLink: {
    url: 'https://www.halocha.io',
  },
  getGroups: {},
  openDocLink: {
    url: 'https://docs.halochat.site/docx/kfOwhxhPznnbXQ0vaj0tbWiSZQ2tZRGT',
  },
  oneSearchByKey: {
    keywords: 'a',
    types: ['user_in_contact', 'user_not_in_contact'],
  },
  getSessionSimpleInfos: {
    page: 1,
    page_size: 10,
  },
};

type RequestMethod = keyof typeof requests;
type RequestResult = {
  argsJson: string;
  response?: unknown;
  error?: string;
};

function safeStringify(value: unknown) {
  if (value === undefined) return '(empty)';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function getInitialRequestArgsJson() {
  return (Object.keys(requests) as RequestMethod[]).reduce(
    (acc, method) => {
      acc[method] = safeStringify(requests[method] ?? {});
      return acc;
    },
    {} as Record<RequestMethod, string>
  );
}

const envOrigin = String(import.meta.env.VITE_EXAMPLE_ORIGIN || '').trim();

export default function App() {
  const [originHistory, setOriginHistory] = useState<string[]>(() => loadOriginHistory());
  const [origin, setOrigin] = useState<string>(() => {
    const hist = loadOriginHistory();
    return hist[0] ?? envOrigin ?? '';
  });
  const [originHistoryOpen, setOriginHistoryOpen] = useState(false);
  const [notifyMethod, setNotifyMethod] = useState<string>('test');
  const [notifyPayload, setNotifyPayload] = useState<string>('{ "message": "Hello, world!" }');
  const [eventName, setEventName] = useState<string>('test');
  const [eventListeners, setEventListeners] = useState<Record<string, string[]>>({});
  const [methodName, setMethodName] = useState<string>('test');
  const [responsePayload, setResponsePayload] = useState<string>(
    '{ "message": "Hello, world!" }'
  );
  const [responses, setResponses] = useState<
    Record<
      string,
      {
        payload: object;
        list: string[];
      }
    >
  >({});
  const [requestArgsJson, setRequestArgsJson] = useState<Record<RequestMethod, string>>(() =>
    getInitialRequestArgsJson()
  );
  const [requestResults, setRequestResults] = useState<Record<string, RequestResult>>({});
  const responsesRef = useRef(responses);
  responsesRef.current = responses;
  const originHistoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (originHistoryRef.current?.contains(e.target as Node)) return;
      setOriginHistoryOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const pushOriginToHistory = useCallback((value: string) => {
    setOriginHistory((prev) => {
      const next = [value, ...prev.filter((v) => v !== value)];
      saveOriginHistory(next);
      return next.slice(0, ORIGIN_HISTORY_MAX);
    });
  }, []);

  const removeOriginFromHistory = useCallback((item: string) => {
    setOriginHistory((prev) => {
      const next = prev.filter((v) => v !== item);
      saveOriginHistory(next);
      return next;
    });
  }, []);

  const bridgeRef = useRef<HaloBridge | null>(null);

  const bridge = useMemo(() => {
    bridgeRef.current?.[Symbol.dispose]();
    return new HaloBridge({
      origin: origin.trim() || window.location.origin,
      handleRequest: async (method, params) => {
        const res = responsesRef.current[method];
        if (!res) {
          return { code: 1, error: 'method not found' };
        }
        setResponses((prev) => ({
          ...prev,
          [method]: {
            ...prev[method],
            list: [...prev[method].list, JSON.stringify(params)],
          },
        }));
        return { code: 0, payload: res.payload };
      },
    });
  }, [origin]);

  bridgeRef.current = bridge;

  const onRunRequest = useCallback(
    async (method: RequestMethod) => {
      const argsJson = requestArgsJson[method] ?? '{}';
      let parsedArgs: unknown = undefined;

      try {
        parsedArgs = JSON.parse(argsJson || '{}') as unknown;
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        setRequestResults((prev) => ({
          ...prev,
          [method]: { argsJson, response: undefined, error: `Invalid JSON args: ${error}` },
        }));
        return;
      }

      try {
        if (!parsedArgs || typeof parsedArgs !== 'object' || Array.isArray(parsedArgs)) {
          throw new Error('args must be a JSON object');
        }

        const params = parsedArgs as Record<string, any>;
        const requestBridge = bridge as unknown as {
          request(method: string, params?: Record<string, any> | undefined): Promise<unknown>;
        };

        const res =
          Object.keys(params).length === 0
            ? await requestBridge.request(method)
            : await requestBridge.request(method, params);

        setRequestResults((prev) => ({
          ...prev,
          [method]: { argsJson, response: res, error: undefined },
        }));
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        setRequestResults((prev) => ({
          ...prev,
          [method]: { argsJson, response: undefined, error },
        }));
      }
    },
    [bridge, requestArgsJson]
  );

  const handleNotifySubmit = () => {
    bridge.notify(notifyMethod as any, JSON.parse(notifyPayload));
  };

  const handleResponseSubmit = () => {
    setResponses((prev) => ({
      ...prev,
      [methodName]: {
        payload: JSON.parse(responsePayload),
        list: [...(prev[methodName]?.list || [])],
      },
    }));
  };

  const onAddListener = useCallback(() => {
    if (eventName in eventListeners) {
      return;
    }

    setEventListeners((prev) => ({ ...prev, [eventName]: [] }));

    bridge.addEventListener(eventName, (e) => {
      const { data } = e as MessageEvent<{ message: string }>;

      setEventListeners((prev) => ({
        ...prev,
        [eventName]: [...(prev[eventName] || []), JSON.stringify(data)],
      }));
    });
  }, [bridge, eventListeners, eventName]);

  useEffect(() => {
    handleResponseSubmit();
    onAddListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app">
      <div className="container">
        <header className="appHeader">
          <div className="titleRow">
            <h1 className="title">Halo Bridge SDK</h1>
            <div className="originField">
              <label className="originLabel" htmlFor="origin-input">
                origin
              </label>
              <div className="originInputRow">
                <TextInput
                  id="origin-input"
                  type="url"
                  value={origin}
                  onChange={(e) => {
                    let trimmed = '';
                    try {
                      trimmed = new URL(e.target.value).origin;
                    } catch {
                      return;
                    }
                    setOrigin(trimmed);
                  }}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v) pushOriginToHistory(v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const v = (e.target as HTMLInputElement).value.trim();
                      if (v) pushOriginToHistory(v);
                    }
                  }}
                  placeholder="e.g. https://web.example.com"
                />
                <div className="originHistoryDropdown" ref={originHistoryRef}>
                  <button
                    type="button"
                    className="originHistorySummary"
                    title="History"
                    onClick={() => setOriginHistoryOpen((prev) => !prev)}
                    aria-expanded={originHistoryOpen}
                  >
                    ⋮
                  </button>
                  {originHistoryOpen && (
                    <div className="originHistoryList">
                      {originHistory.length === 0 ? (
                        <p className="originHistoryEmpty">No history</p>
                      ) : (
                        originHistory.map((item) => (
                          <div key={item} className="originHistoryItem">
                            <button
                              type="button"
                              className="originHistorySelect"
                              onClick={() => {
                                setOrigin(item);
                                setOriginHistoryOpen(false);
                              }}
                              title={item}
                            >
                              {item}
                            </button>
                            <button
                              type="button"
                              className="originHistoryDelete"
                              onClick={(e) => {
                                e.preventDefault();
                                removeOriginFromHistory(item);
                              }}
                              title="Remove from history"
                              aria-label={`Remove ${item} from history`}
                            >
                              ×
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <p className="subtitle">
            A tiny playground for requesting data, registering responses, sending notifications,
            and listening to events through the bridge.
          </p>
        </header>

        <div className="grid">
          <Card title="request" hint="Fetch data from host via request()" ariaLabel="request">
            <Stack>
              <Field
                label="request list"
                labelRight={
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setRequestResults({})}
                    disabled={Object.keys(requestResults).length === 0}
                  >
                    Clear
                  </Button>
                }
              >
                <Items>
                  {(Object.keys(requests) as RequestMethod[]).map((method) => {
                    const result = requestResults[method];
                    const responseText = result?.error
                      ? `ERROR: ${result.error}`
                      : safeStringify(result?.response);
                    const argsJson = requestArgsJson[method] ?? '{}';

                    return (
                      <ItemCard
                        key={method}
                        title={
                          <Button type="button" onClick={() => onRunRequest(method)}>
                            {method}
                          </Button>
                        }
                      >
                        <ItemMeta>args</ItemMeta>
                        <TextArea
                          value={argsJson}
                          onChange={(e) =>
                            setRequestArgsJson((prev) => ({
                              ...prev,
                              [method]: e.target.value,
                            }))
                          }
                          spellCheck={false}
                          className="codeLike"
                        />

                        <ItemMeta>response</ItemMeta>
                        <CodeBlock>{responseText}</CodeBlock>
                      </ItemCard>
                    );
                  })}
                </Items>
              </Field>
            </Stack>
          </Card>

          <Card
            title="response"
            dotVariant="green"
            hint="Mock handler payloads for host → web requests"
            ariaLabel="response"
          >
            <Stack>
              <form
                className="stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleResponseSubmit();
                }}
              >
                <Field label="Method" htmlFor="response-method-input">
                  <TextInput
                    id="response-method-input"
                    type="text"
                    value={methodName}
                    onChange={(e) => setMethodName(e.target.value)}
                    placeholder="test"
                  />
                </Field>

                <Field label="Payload (JSON)" htmlFor="response-payload-textarea">
                  <TextArea
                    id="response-payload-textarea"
                    value={responsePayload}
                    onChange={(e) => setResponsePayload(e.target.value)}
                    spellCheck={false}
                  />
                </Field>

                <Row>
                  <Button type="submit">Add response</Button>
                </Row>
              </form>

              <Divider />

              <Field label="response content">
                {Object.keys(responses).length === 0 ? (
                  <Empty />
                ) : (
                  <Items>
                    {Object.entries(responses).map(([name, { payload, list }]) => (
                      <ItemCard key={name} title={name}>
                        <ItemMeta>payload</ItemMeta>
                        <CodeBlock>{JSON.stringify(payload, null, 2)}</CodeBlock>
                        <ItemMeta>calls</ItemMeta>
                        <List as="ol">
                          {list.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </List>
                      </ItemCard>
                    ))}
                  </Items>
                )}
              </Field>
            </Stack>
          </Card>

          <Card title="send notify" hint="Send notify() to host" ariaLabel="send notify">
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                handleNotifySubmit();
              }}
            >
              <Field label="Method" htmlFor="notify-method-input">
                <TextInput
                  id="notify-method-input"
                  type="text"
                  value={notifyMethod}
                  onChange={(e) => setNotifyMethod(e.target.value)}
                  placeholder="test"
                />
              </Field>

              <Field label="Payload (JSON)" htmlFor="notify-payload-textarea">
                <TextArea
                  id="notify-payload-textarea"
                  value={notifyPayload}
                  onChange={(e) => setNotifyPayload(e.target.value)}
                  spellCheck={false}
                />
              </Field>

              <Row>
                <Button type="submit">Send notify</Button>
              </Row>
            </form>
          </Card>

          <Card
            title="listen notify"
            dotVariant="green"
            hint="Register addEventListener() and inspect events"
            ariaLabel="listen notify"
          >
            <Stack>
              <Field label="Event name" htmlFor="event-name-input">
                <TextInput
                  id="event-name-input"
                  type="text"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="test"
                />
              </Field>

              <Row>
                <Button variant="secondary" type="button" onClick={onAddListener}>
                  Add listener
                </Button>
              </Row>

              <Divider />

              <Field label="events">
                {Object.keys(eventListeners).length === 0 ? (
                  <Empty />
                ) : (
                  <Items>
                    {Object.entries(eventListeners).map(([name, content]) => (
                      <ItemCard key={name} title={name}>
                        <List as="ol">
                          {content.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </List>
                      </ItemCard>
                    ))}
                  </Items>
                )}
              </Field>
            </Stack>
          </Card>
        </div>
      </div>
    </div>
  );
}
